import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import clientLogger from '../utils/clientLogger';
import { prysmDB } from '../utils/database';
import apiService from '../services/apiService';
import { getVisitorUUID } from '../utils/uuidManager';
import { BACKEND_URL } from '../config';
import { useAwsRum } from '../hooks/useAwsRum';
import ErrorModal from './ErrorModal';

// Square SDK - global window object will be available at runtime

/**
 * Square Payment Card Component
 * 
 * This component integrates with Square's Web SDK v2.1.0 to handle secure payment processing.
 * Based on the working implementation from example_square directory.
 */
export const SquarePaymentCard = ({
    amount = 2500,
    currency = 'USD',
    onPaymentSuccess,
    onPaymentError,
    isProcessing = false,
    userData = {}
}) => {
    // ================================
    // REFS
    // ================================

    const cardRef = useRef(null); // DOM element
    const paymentsRef = useRef(null); // Square payments instance
    const cardInstanceRef = useRef(null); // Square card instance
    const hasInitRef = useRef(false); // Prevents duplicate initialization
    const navigate = useNavigate();
    
    // ================================
    // AWS RUM TRACKING
    // ================================
    
    const {
        trackEvent,
        trackPayment,
        trackSquare,
        trackError,
        trackPerformance,
        trackFormErrors,
        addUserAttributes
    } = useAwsRum('SquarePaymentCard');

    // ================================
    // STATE MANAGEMENT
    // ================================

    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState(null);
    const [error, setError] = useState(null);
    const [consolidatedErrors, setConsolidatedErrors] = useState(null);
    const [billingErrors, setBillingErrors] = useState([]);
    const [cardToken, setCardToken] = useState(null);
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [paymentSubmitted, setPaymentSubmitted] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState('explorer');
    const [promoCode, setPromoCode] = useState('');
    const [promoApplied, setPromoApplied] = useState(false);
    const [promoDiscount, setPromoDiscount] = useState(0);
    const [promoError, setPromoError] = useState('');
    const [paymentFailed, setPaymentFailed] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const [lastError, setLastError] = useState(null);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [modalError, setModalError] = useState(null);

    // Billing address state
    const [billingEmail, setBillingEmail] = useState('');
    const [billingPhone, setBillingPhone] = useState('');
    const [billingCity, setBillingCity] = useState('');
    const [billingState, setBillingState] = useState('');
    const [billingZip, setBillingZip] = useState('');
    const [billingCountry, setBillingCountry] = useState('US'); // Default to US

    // ================================
    // SUBSCRIPTION PLANS
    // ================================

    const plans = {
        explorer: {
            id: 'explorer',
            name: 'Explorer Plan',
            tagline: 'Start Here',
            price: 4.95, // $4.95 in cents
            recurringPrice: 9.95, // $9.95 in cents
            period: 'month',
            features: [
                'Perfect for exploring the community',
                'Full community access',
                'Verified member profiles',
                'Secure messaging',
                'Event notifications',
                'Auto-renews monthly'
            ]
        },
        seeker: {
            id: 'seeker',
            name: 'Seeker Plan',
            tagline: 'Best Value',
            price: 18.00, // $18.00 in cents
            recurringPrice: 23.88, // $23.88 in cents
            period: 'quarter',
            savings: 'Save 33%',
            features: [
                'For deeper connections',
                'Everything in Explorer',
                'Priority support',
                'Advanced matching',
                'Profile spotlighting',
                'Auto-renews every 3 months'
            ]
        }
    };

    // ================================
    // UTILITY FUNCTIONS
    // ================================

    /**
     * Format phone number to (XXX) XXX-XXXX format for Square validation
     * @param {string} phoneNumber - Raw phone number string
     * @returns {string} Formatted phone number
     */
    const formatPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return '';

        // Remove all non-digit characters
        const digits = phoneNumber.replace(/\D/g, '');

        // Format as (XXX) XXX-XXXX
        if (digits.length === 10) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
        } else if (digits.length === 11 && digits[0] === '1') {
            // Handle US numbers with country code
            return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
        }

        // Return original if can't format
        return phoneNumber;
    };

    // ================================
    // ENTERPRISE ERROR HANDLING
    // ================================

    /**
     * Get simple user-friendly error message
     */
    const getSimpleErrorMessage = (error, context = 'Payment Processing') => {
        const message = error.message || error.toString();
        
        if (message.includes('DECLINED') || message.includes('declined')) {
            return 'Your card was declined. Please try a different payment method or contact your bank.';
        }
        if (message.includes('INVALID') || message.includes('invalid')) {
            return 'Please check your card information and try again.';
        }
        if (message.includes('EXPIRED') || message.includes('expired')) {
            return 'Your card has expired. Please use a different card.';
        }
        if (message.includes('INSUFFICIENT') || message.includes('insufficient')) {
            return 'Insufficient funds. Please try a different payment method.';
        }
        if (message.includes('network') || message.includes('connection')) {
            return 'Connection issue. Please check your internet and try again.';
        }
        
        return 'Payment failed. Please try again or contact support if the problem continues.';
    };

    /**
     * Translate Square SDK error codes to plain English
     */
    const translateSquareError = (result) => {
        const errors = result.errors || [];
        const translatedErrors = [];

        errors.forEach(error => {
            const code = error.code;
            const detail = error.detail;
            const field = error.field;

            switch (code) {
                case 'INVALID_REQUEST_ERROR':
                    translatedErrors.push({
                        code: code,
                        field: field || 'Card Information',
                        message: detail || 'Invalid card information provided',
                        userMessage: 'Please check your card information and try again'
                    });
                    break;

                case 'CARD_DECLINED':
                    translatedErrors.push({
                        code: code,
                        field: field || 'Card',
                        message: detail || 'Card was declined',
                        userMessage: 'Your card was declined. Please try a different payment method or contact your bank.'
                    });
                    break;

                case 'INVALID_EXPIRATION':
                    translatedErrors.push({
                        code: code,
                        field: 'Expiration Date',
                        message: detail || 'Invalid expiration date',
                        userMessage: 'Please check the expiration date on your card'
                    });
                    break;

                case 'INVALID_EXPIRATION_YEAR':
                    translatedErrors.push({
                        code: code,
                        field: 'Expiration Year',
                        message: detail || 'Invalid expiration year',
                        userMessage: 'Please check the expiration year on your card'
                    });
                    break;

                case 'INVALID_EXPIRATION_DATE':
                    translatedErrors.push({
                        code: code,
                        field: 'Expiration Date',
                        message: detail || 'Invalid expiration date',
                        userMessage: 'Please check the expiration date on your card'
                    });
                    break;

                case 'INVALID_POSTAL_CODE':
                    translatedErrors.push({
                        code: code,
                        field: 'Postal Code',
                        message: detail || 'Invalid postal code',
                        userMessage: 'Please check your postal code'
                    });
                    break;

                case 'INVALID_EMAIL_ADDRESS':
                    translatedErrors.push({
                        code: code,
                        field: 'Email',
                        message: detail || 'Invalid email address',
                        userMessage: 'Please enter a valid email address'
                    });
                    break;

                case 'INVALID_PHONE_NUMBER':
                    translatedErrors.push({
                        code: code,
                        field: 'Phone Number',
                        message: detail || 'Invalid phone number',
                        userMessage: 'Please enter a valid phone number'
                    });
                    break;

                case 'CARD_NOT_SUPPORTED':
                    translatedErrors.push({
                        code: code,
                        field: 'Card Type',
                        message: detail || 'Card type not supported',
                        userMessage: 'This card type is not supported. Please try a different card.'
                    });
                    break;

                case 'UNSUPPORTED_CARD_BRAND':
                    translatedErrors.push({
                        code: code,
                        field: 'Card Brand',
                        message: detail || 'Unsupported card brand',
                        userMessage: 'This card brand is not supported. Please try a different card.'
                    });
                    break;

                case 'INVALID_CARD_DATA':
                    translatedErrors.push({
                        code: code,
                        field: 'Card Information',
                        message: detail || 'Invalid card data',
                        userMessage: 'Please check your card number, expiration date, and CVV'
                    });
                    break;

                case 'INVALID_CARD_NUMBER':
                    translatedErrors.push({
                        code: code,
                        field: 'Card Number',
                        message: detail || 'Invalid card number',
                        userMessage: 'Please check your card number'
                    });
                    break;

                case 'INVALID_CVV':
                    translatedErrors.push({
                        code: code,
                        field: 'CVV',
                        message: detail || 'Invalid CVV',
                        userMessage: 'Please check the CVV (security code) on your card'
                    });
                    break;

                case 'INSUFFICIENT_FUNDS':
                    translatedErrors.push({
                        code: code,
                        field: 'Card',
                        message: detail || 'Insufficient funds',
                        userMessage: 'Your card has insufficient funds. Please try a different payment method.'
                    });
                    break;

                case 'EXPIRED_CARD':
                    translatedErrors.push({
                        code: code,
                        field: 'Card',
                        message: detail || 'Card has expired',
                        userMessage: 'Your card has expired. Please use a different card.'
                    });
                    break;

                case 'PROCESSING_ERROR':
                    translatedErrors.push({
                        code: code,
                        field: 'Payment Processing',
                        message: detail || 'Payment processing error',
                        userMessage: 'There was an error processing your payment. Please try again.'
                    });
                    break;

                case 'TEMPORARY_ERROR':
                    translatedErrors.push({
                        code: code,
                        field: 'Payment System',
                        message: detail || 'Temporary system error',
                        userMessage: 'There is a temporary issue with the payment system. Please try again in a few moments.'
                    });
                    break;

                case 'RATE_LIMIT_ERROR':
                    translatedErrors.push({
                        code: code,
                        field: 'Payment System',
                        message: detail || 'Rate limit exceeded',
                        userMessage: 'Too many requests. Please wait a moment and try again.'
                    });
                    break;

                case 'UNAUTHORIZED':
                    translatedErrors.push({
                        code: code,
                        field: 'Authentication',
                        message: detail || 'Unauthorized request',
                        userMessage: 'There was an authentication error. Please refresh the page and try again.'
                    });
                    break;

                case 'FORBIDDEN':
                    translatedErrors.push({
                        code: code,
                        field: 'Authorization',
                        message: detail || 'Forbidden request',
                        userMessage: 'Access denied. Please contact support if this continues.'
                    });
                    break;

                case 'NOT_FOUND':
                    translatedErrors.push({
                        code: code,
                        field: 'Resource',
                        message: detail || 'Resource not found',
                        userMessage: 'Payment resource not found. Please refresh the page and try again.'
                    });
                    break;

                case 'INTERNAL_SERVER_ERROR':
                    translatedErrors.push({
                        code: code,
                        field: 'Server',
                        message: detail || 'Internal server error',
                        userMessage: 'There was a server error. Please try again in a few moments.'
                    });
                    break;

                case 'SERVICE_UNAVAILABLE':
                    translatedErrors.push({
                        code: code,
                        field: 'Service',
                        message: detail || 'Service unavailable',
                        userMessage: 'The payment service is temporarily unavailable. Please try again later.'
                    });
                    break;

                default:
                    translatedErrors.push({
                        code: code || 'UNKNOWN_ERROR',
                        field: field || 'Payment',
                        message: detail || 'Unknown error occurred',
                        userMessage: 'An unexpected error occurred. Please try again or contact support.'
                    });
                    break;
            }
        });

        return translatedErrors;
    };

    /**
     * Get specific solutions based on error type and context
     */
    const getErrorSolutions = (error, context) => {
        const errorMessage = error.message?.toLowerCase() || '';
        const errorName = error.name || '';

        // Square SDK specific errors
        if (errorMessage.includes('invalid') && errorMessage.includes('card')) {
            return [
                'Check that the card number is entered correctly',
                'Verify the expiration date and CVV',
                'Ensure the card is not expired',
                'Try a different payment method if available'
            ];
        }

        if (errorMessage.includes('network') || errorMessage.includes('connection')) {
            return [
                'Check your internet connection',
                'Try refreshing the page',
                'Ensure your firewall allows the payment system',
                'Try again in a few moments'
            ];
        }

        if (errorMessage.includes('declined') || errorMessage.includes('rejected')) {
            return [
                'Contact your bank to verify the card is active',
                'Check if you have sufficient funds',
                'Verify billing address matches card registration',
                'Try a different payment method'
            ];
        }

        if (errorMessage.includes('sdk') || errorMessage.includes('square')) {
            return [
                'Refresh the page to reload the payment system',
                'Clear your browser cache and try again',
                'Try using a different browser',
                'Contact support if the problem persists'
            ];
        }

        if (errorMessage.includes('promo') || errorMessage.includes('discount')) {
            return [
                'Check that the promotional code is spelled correctly',
                'Verify the code is still valid and not expired',
                'Ensure the code applies to your selected plan',
                'Try entering the code again without spaces'
            ];
        }

        // Generic solutions
        return [
            'Try refreshing the page and starting over',
            'Check your internet connection',
            'Ensure all payment information is correct',
            'Try using a different browser if problems persist'
        ];
    };

    /**
     * Handle Square tokenization errors with detailed translation
     */
    const handleSquareTokenizationError = (result) => {
        console.error('[SquarePaymentCard] Square tokenization failed:', result);

        const translatedErrors = translateSquareError(result);
        const errorDetails = {
            summary: 'Card Tokenization Failed',
            timestamp: new Date().toISOString(),
            context: 'Square Card Tokenization',
            details: translatedErrors.map(translatedError => ({
                field: translatedError.field,
                originalValue: 'Card Information',
                errorType: translatedError.code,
                errorMessage: translatedError.message,
                userMessage: translatedError.userMessage,
                possibleSolutions: getSquareErrorSolutions(translatedError.code)
            })),
            generalAdvice: [
                'Please check your card information and try again',
                'Ensure your card is not expired and has sufficient funds',
                'Try a different payment method if the problem persists',
                'Contact your bank if you continue to have issues'
            ]
        };

        setConsolidatedErrors(errorDetails);
        setError(translatedErrors.map(e => e.userMessage).join('; '));

        if (onPaymentError) {
            onPaymentError(new Error(translatedErrors.map(e => e.userMessage).join('; ')));
        }
    };

    /**
     * Get specific solutions for Square error codes
     */
    const getSquareErrorSolutions = (errorCode) => {
        switch (errorCode) {
            case 'CARD_DECLINED':
                return [
                    'Contact your bank to verify the card is active',
                    'Check if you have sufficient funds available',
                    'Verify billing address matches card registration',
                    'Try a different payment method'
                ];
            case 'INVALID_EXPIRATION':
            case 'INVALID_EXPIRATION_YEAR':
            case 'INVALID_EXPIRATION_DATE':
                return [
                    'Check the expiration date on your card',
                    'Ensure the month and year are correct',
                    'Make sure the card has not expired',
                    'Try entering the date again'
                ];
            case 'INVALID_CVV':
                return [
                    'Check the 3-digit CVV on the back of your card',
                    'For American Express, use the 4-digit code on the front',
                    'Ensure you are entering only numbers',
                    'Try entering the CVV again'
                ];
            case 'INVALID_CARD_NUMBER':
                return [
                    'Check that you entered the card number correctly',
                    'Remove any spaces or dashes',
                    'Ensure all digits are entered',
                    'Try typing the number again'
                ];
            case 'INSUFFICIENT_FUNDS':
                return [
                    'Check your account balance',
                    'Try a different payment method',
                    'Contact your bank to verify available funds',
                    'Consider using a different card'
                ];
            case 'EXPIRED_CARD':
                return [
                    'Use a different card that is not expired',
                    'Check the expiration date on your current card',
                    'Contact your bank for a replacement card'
                ];
            case 'CARD_NOT_SUPPORTED':
            case 'UNSUPPORTED_CARD_BRAND':
                return [
                    'Try using a Visa, MasterCard, American Express, or Discover card',
                    'Check with your bank about card acceptance',
                    'Try a different payment method'
                ];
            default:
                return [
                    'Check all card information carefully',
                    'Try a different payment method',
                    'Contact support if the problem continues'
                ];
        }
    };

    /**
     * Handle errors with simple user feedback
     */
    const handleSimpleError = (error, context = 'Payment Processing') => {
        console.error(`[SquarePaymentCard] ${context} Error:`, error);
        console.log(`[SquarePaymentCard] Error details:`, {
            message: error.message,
            squareErrorCode: error.squareErrorCode,
            squareErrorDetail: error.squareErrorDetail,
            squareErrorCategory: error.squareErrorCategory,
            context: context
        });
        
        console.log(`[SquarePaymentCard] handleSimpleError called with context:`, context);

        // Log error to server
        clientLogger.logError(context.toLowerCase().replace(/\s+/g, '_'), `${context} failed: ${error.message}`, error, {
            action: 'square_payment_error',
            component: 'SquarePaymentCard'
        });

        const userMessage = getSimpleErrorMessage(error, context);
        
        // Show modal for payment submission errors, inline for others
        if (context === 'Payment Processing') {
            console.log(`[SquarePaymentCard] Setting modal error:`, {
                error: error,
                userMessage: userMessage,
                willShowModal: true,
                currentShowErrorModal: showErrorModal
            });
            setModalError(error);
            setShowErrorModal(true);
            setPaymentFailed(true);
            setLastError(error);
        } else {
            console.log(`[SquarePaymentCard] Setting inline error for context:`, context);
            setError(userMessage);
            setConsolidatedErrors(null); // Clear complex errors
        }

        // Call external error handler if provided
        if (onPaymentError) {
            onPaymentError(error);
        }
    };

    /**
     * Clear all error states
     */
    const clearErrors = () => {
        setError(null);
        setConsolidatedErrors(null);
        setPromoError('');
        setPaymentFailed(false);
        setLastError(null);
        setShowErrorModal(false);
        setModalError(null);
    };

    // ================================
    // CONFIGURATION
    // ================================

    const config = {
        environment: process.env.REACT_APP_SQUARE_ENVIRONMENT || 'sandbox',
        applicationId: process.env.REACT_APP_SQUARE_APPLICATION_ID,
        locationId: process.env.REACT_APP_SQUARE_LOCATION_ID,
        sdkUrl: process.env.REACT_APP_SQUARE_SDK_URL || 'https://web.squarecdn.com/v1/square.js',
        backendUrl: process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000'
    };

    // Debug configuration
    console.log('[SquarePaymentCard] Configuration loaded', {
        environment: config.environment,
        hasApplicationId: !!config.applicationId,
        hasLocationId: !!config.locationId,
        sdkUrl: config.sdkUrl,
        backendUrl: config.backendUrl
    });

    // ================================
    // VALIDATION FUNCTIONS
    // ================================

    const validateConfiguration = () => {
        const errors = [];

        if (!config.applicationId) {
            errors.push('Square Application ID is required');
        }

        if (!config.locationId) {
            errors.push('Square Location ID is required');
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    };

    const validateBillingData = () => {
        const errors = [];
        const currentPlan = plans[selectedPlan];
        const finalAmount = Math.max(0, currentPlan.price - promoDiscount);

        // Always require email and phone
        if (!billingEmail.trim()) {
            errors.push('Email is required');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billingEmail.trim())) {
            errors.push('Please enter a valid email address');
        }

        if (!billingPhone.trim()) {
            errors.push('Phone number is required');
        } else if (!/^\(\d{3}\) \d{3}-\d{4}$/.test(billingPhone.trim())) {
            errors.push('Phone number must be in format (XXX) XXX-XXXX');
        }

        // Only require full billing address if payment is needed
        if (finalAmount > 0) {
            if (!billingCity.trim()) {
                errors.push('City is required');
            }

            if (!billingState.trim()) {
                errors.push('State is required');
            }

            if (!billingZip.trim()) {
                errors.push('ZIP/Postal code is required');
            } else {
                const zip = billingZip.trim();
                const isUSZip = /^\d{5}$/.test(zip);
                const isCanadianPostal = /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/.test(zip);
                if (!isUSZip && !isCanadianPostal) {
                    errors.push('Please enter a valid ZIP code (12345) or Canadian postal code (A1A 1A1)');
                }
            }

            if (!billingCountry.trim()) {
                errors.push('Country is required');
            }
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    };

    /**
     * Helper function to get field-specific error message
     */
    const getFieldError = (fieldName) => {
        return billingErrors.find(error =>
            error.toLowerCase().includes(fieldName.toLowerCase())
        ) || null;
    };

    // ================================
    // PROMOTIONAL CODE HANDLING
    // ================================

    const validatePromoCode = useCallback(async (code) => {
        if (!code.trim()) {
            setPromoError('');
            setPromoApplied(false);
            setPromoDiscount(0);
            return;
        }

        // Reset discount immediately when starting validation to ensure UI updates
        setPromoDiscount(0);
        setPromoApplied(false);
        setPromoError('');

        try {
            // Get user data from registration storage
            const registrationData = await prysmDB.get('registration_data', 'current_registration');
            if (!registrationData) {
                setPromoError('Please complete registration first');
                return;
            }

            const userEmail = registrationData.email;
            const userGender = registrationData.gender;

            if (!userEmail) {
                setPromoError('Email address not found in registration data');
                return;
            }

            if (!userGender) {
                setPromoError('Gender information not found in registration data');
                return;
            }

            console.log('[SquarePaymentCard] Validating promo code with server:', {
                promoCode: code.toUpperCase(),
                email: userEmail,
                gender: userGender
            });

            // Call server API to validate promo code
            const response = await apiService.checkPromoCode(code.toUpperCase(), userEmail, userGender);

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to validate promotional code');
            }

            const result = await response.json();
            console.log('[SquarePaymentCard] Server promo validation response:', result);

            if (result.status === true) {
                setPromoApplied(true);
                setPromoError('');

                // Calculate discount amount using percentage from server
                const currentPlan = plans[selectedPlan];
                const discountPercent = result.discount_percent || 0;
                const discountAmount = Math.min(currentPlan.price, Math.round(currentPlan.price * (discountPercent / 100) * 100) / 100);

                setPromoDiscount(discountAmount);

                // Persist promo code to storage
                try {
                    if (registrationData) {
                        registrationData.promoCode = code.toUpperCase();
                        registrationData.promoApplied = true;
                        registrationData.promoDiscount = discountAmount;
                        registrationData.promoDescription = `${discountPercent}% discount`;
                        registrationData.promoType = 'percentage';
                        registrationData.promoTimestamp = new Date().toISOString();
                        registrationData.promoDiscountPercent = discountPercent;
                        await prysmDB.set('registration_data', 'current_registration', registrationData);

                        // Dispatch event to notify other components
                        window.dispatchEvent(new CustomEvent('registrationDataUpdated', { detail: registrationData }));

                        console.log('[SquarePaymentCard] Promo code persisted to storage:', {
                            code: code.toUpperCase(),
                            discount: discountAmount,
                            discountPercent: discountPercent,
                            description: `${discountPercent}% discount`
                        });
                    }
                } catch (error) {
                    console.error('[SquarePaymentCard] Error persisting promo code to storage:', error);
                }

                console.log('[SquarePaymentCard] Promo code applied:', {
                    code,
                    discount: discountAmount,
                    discountPercent: discountPercent,
                    message: result.message
                });
            } else {
                setPromoError(result.message || 'Invalid promotional code');
                // Discount already reset at start of function
            }
        } catch (error) {
            handleSimpleError(error, 'Promotional Code Validation');
            setPromoError(error.message || 'Error validating promotional code');
            // Discount already reset at start of function

            // Log error to server
            clientLogger.logValidationError('promoCode', error, {
                promoCode: code,
                action: 'validate_promo_code_server'
            });
        }
    }, [selectedPlan, plans]);

    /** @type {React.MutableRefObject<NodeJS.Timeout|null>} Ref to store debounce timeout ID */
    const debounceTimeoutRef = useRef(null);

    /**
     * Handles promotional code input changes with debounced validation
     * Prevents multiple API calls by canceling previous timeout before setting new one
     * @param {React.ChangeEvent<HTMLInputElement>} e - Input change event
     */
    const handlePromoCodeChange = useCallback((e) => {
        const code = e.target.value;
        setPromoCode(code);
        setPromoError('');

        // Clear previous timeout to prevent multiple API calls
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        // Auto-validate as user types (debounced after 500ms of inactivity)
        if (code.length >= 3) {
            debounceTimeoutRef.current = setTimeout(() => {
                validatePromoCode(code);
            }, 500);
        } else if (code.length === 0) {
            setPromoApplied(false);
            setPromoDiscount(0);
        }
    }, [validatePromoCode]);

    const removePromoCode = useCallback(() => {
        setPromoCode('');
        setPromoApplied(false);
        setPromoDiscount(0);
        setPromoError('');

        // Remove promo code from storage
        const removePromoFromstorage = async () => {
            try {
                const registrationData = await prysmDB.get('registration_data', 'current_registration');
                if (registrationData) {
                    delete registrationData.promoCode;
                    delete registrationData.promoApplied;
                    delete registrationData.promoDiscount;
                    delete registrationData.promoDescription;
                    delete registrationData.promoType;
                    delete registrationData.promoTimestamp;
                    await prysmDB.set('registration_data', 'current_registration', registrationData);

                    // Dispatch event to notify other components
                    window.dispatchEvent(new CustomEvent('registrationDataUpdated', { detail: registrationData }));

                    console.log('[SquarePaymentCard] Promo code removed from storage');
                }
            } catch (error) {
                console.error('[SquarePaymentCard] Error removing promo code from storage:', error);
            }
        };

        removePromoFromstorage();
    }, []);

    // Clear errors when user interacts with card form
    const handleCardInteraction = useCallback(() => {
        clearErrors();
    }, []);

    // ================================
    // PAYMENT PROCESSING
    // ================================

    const handleTokenReceived = useCallback((token, completeResult = null) => {
        console.log('[SquarePaymentCard] Card token received');
        setCardToken(token);
        clearErrors();
        
        // Track successful card validation
        trackPayment.validation({
            success: true,
            tokenLength: token ? token.length : 0,
            hasCompleteResult: !!completeResult
        });

        // Store complete Square response in storage under square_nonce_info key
        if (completeResult) {
            const saveSquareNonceInfo = async () => {
                try {
                    const nonceData = {
                        ...completeResult,
                        timestamp: new Date().toISOString(),
                        validationStatus: 'success'
                    };
                    await prysmDB.set('square_nonce_info', 'current_nonce', nonceData);

                    console.log('[SquarePaymentCard] Square nonce info saved to storage:', {
                        token: token,
                        status: completeResult.status,
                        timestamp: new Date().toISOString()
                    });
                } catch (error) {
                    console.error('[SquarePaymentCard] Error saving Square nonce info to storage:', error);
                }
            };

            saveSquareNonceInfo();
        }
    }, []);

    const handlePaymentError = useCallback((errorMessage) => {
        const error = new Error(errorMessage);
        handleSimpleError(error, 'Payment Token Generation');
        setCardToken(null);
        
        // Track payment error
        trackPayment.error({
            errorMessage: errorMessage,
            errorType: 'token_generation',
            step: 'card_validation'
        });

        // Clear Square nonce info from storage when there's an error
        const clearSquareNonceInfo = async () => {
            try {
                await prysmDB.delete('square_nonce_info', 'current_nonce');
                console.log('[SquarePaymentCard] Square nonce info cleared from storage due to error');
            } catch (error) {
                console.error('[SquarePaymentCard] Error clearing Square nonce info from storage:', error);
            }
        };

        clearSquareNonceInfo();
    }, []);


    const processPayment = useCallback(async (token, billingAddress) => {
        const paymentStartTime = Date.now();
        
        try {
            setIsProcessingPayment(true);
            
            // Track payment processing start
            trackPayment.start({
                amount: amount,
                currency: currency,
                selectedPlan: selectedPlan,
                hasPromoCode: !!promoCode,
                promoApplied: promoApplied,
                promoDiscount: promoDiscount
            });

            // Get current visitor UUID
            const visitorUUID = await getVisitorUUID();
            if (!visitorUUID) {
                throw new Error('Visitor UUID not found. Please refresh the page and try again.');
            }

            // Get user email from registration data
            const registrationData = await prysmDB.get('registration_data', 'current_registration');
            const userEmail = registrationData?.email;
            if (!userEmail) {
                throw new Error('User email not found. Please complete registration first.');
            }

            const currentPlan = plans[selectedPlan];

            const finalAmount = Math.max(0, currentPlan.price - promoDiscount);

            // Get registration response from storage - try multiple possible keys
            let registrationResponse = null;
            try {
                // First try the specific registration_response key
                registrationResponse = await prysmDB.get('registration_data', 'registration_response');
                console.log('[SquarePaymentCard] Attempt 1 - prysmDB.get result:', registrationResponse);
                
                // If not found, try getting it from the main registration data
                if (!registrationResponse) {
                    const registrationData = await prysmDB.get('registration_data', 'current_registration');
                    registrationResponse = registrationData?.registrationResponse || registrationData?.squareCustomerResponse;
                    console.log('[SquarePaymentCard] Attempt 2 - from current_registration:', registrationResponse);
                }
                
                // If still not found, try direct localStorage access as fallback
                if (!registrationResponse) {
                    const directAccess = localStorage.getItem('prysm-registration-db_registration_data_registration_response');
                    if (directAccess) {
                        registrationResponse = JSON.parse(directAccess);
                        console.log('[SquarePaymentCard] Attempt 3 - direct localStorage access:', registrationResponse);
                    }
                }
                
                console.log('[SquarePaymentCard] Final registration response loaded:', registrationResponse);
            } catch (error) {
                console.warn('[SquarePaymentCard] Could not load registration response from storage:', error);
            }

            const paymentData = {
                'data-type': 'payment',
                visitorUUID: visitorUUID,
                email: userEmail,
                cardToken: token,
                amount: finalAmount,
                originalAmount: currentPlan.price,
                discountAmount: promoDiscount,
                promoCode: promoCode || null,
                currency: currency,
                planId: selectedPlan,
                planName: currentPlan.name,
                recurringPrice: currentPlan.recurringPrice,
                period: currentPlan.period,
                billingAddress: billingAddress,
                // Additional plan and pricing details
                currentPlan: currentPlan,
                currentPlanPrice: currentPlan.price,
                promoDiscount: promoDiscount,
                finalAmount: finalAmount,
                // Registration response from Square customer processing
                registrationResponse: registrationResponse,
                timestamp: new Date().toISOString()
            };

            // Process payment using API service
            const response = await apiService.processPayment(paymentData, {
                planId: selectedPlan,
                amount: finalAmount,
                promoCode: promoApplied ? promoCode : null,
                cardToken: token ? 'present' : 'missing',
                billingAddress: billingAddress ? 'present' : 'missing'
            });

            if (!response.ok) {
                // Handle different types of errors
                let errorData;
                try {
                    errorData = await response.json();
                } catch (parseError) {
                    console.error('[SquarePaymentCard] Failed to parse error response:', parseError);
                    errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
                }

                if (response.status === 400) {
                    if (errorData.error === 'Validation failed' && errorData.details) {
                        // Format validation errors for user display
                        const validationErrors = Object.entries(errorData.details)
                            .map(([field, message]) => `${field}: ${message}`)
                            .join('; ');
                        throw new Error(`Validation Error: ${validationErrors}`);
                    } else {
                        // Handle both direct error responses and nested error responses
                        const errorInfo = errorData.error || errorData;
                        const errorMessage = errorInfo.message || errorData.message || errorData.error || `Payment validation failed: ${response.status} ${response.statusText}`;
                        
                        const error = new Error(errorMessage);
                        error.squareErrorCode = errorInfo.squareErrorCode || errorInfo.code || errorData.squareErrorCode || errorData.error;
                        error.squareErrorDetail = errorInfo.squareErrorDetail || errorData.squareErrorDetail;
                        error.squareErrorCategory = errorInfo.squareErrorCategory || errorData.squareErrorCategory;
                        throw error;
                    }
                } else if (response.status === 500) {
                    // System failure - alert support
                    const errorMessage = `System Error: Our payment system is currently experiencing technical difficulties. Please contact support@ourprysm.com immediately for assistance. Error: ${response.status} ${response.statusText}`;

                    // Log the system error for debugging
                    console.error('[SquarePaymentCard] System Error 500:', {
                        status: response.status,
                        statusText: response.statusText,
                        timestamp: new Date().toISOString(),
                        userAgent: navigator.userAgent
                    });

                    throw new Error(errorMessage);
                } else {
                    // For other status codes, try to use the error message from response
                    const errorMessage = errorData.message || errorData.error || `Payment failed: ${response.status} ${response.statusText}`;
                    const error = new Error(errorMessage);
                    error.squareErrorCode = errorData.squareErrorCode || errorData.error;
                    error.squareErrorDetail = errorData.squareErrorDetail;
                    error.squareErrorCategory = errorData.squareErrorCategory;
                    throw error;
                }
            }

            const result = await response.json();

            if (result.success || result.status === 'success') {
                console.log('[SquarePaymentCard] Payment successful:', result);
                
                // Clear retry states on success
                setPaymentFailed(false);
                setRetryCount(0);
                setLastError(null);
                
                // Track successful payment
                const paymentDuration = Date.now() - paymentStartTime;
                trackPayment.success({
                    transactionId: result.transactionId || result.paymentId,
                    amount: finalAmount,
                    originalAmount: currentPlan.price,
                    discountAmount: promoDiscount,
                    promoCode: promoCode,
                    selectedPlan: selectedPlan,
                    processingDuration: paymentDuration,
                    retryCount: retryCount
                });
                
                // Track performance metric
                trackPerformance('payment_processing_duration', paymentDuration, 'ms', {
                    success: true,
                    amount: finalAmount
                });

                // Store complete Square response to storage
                const savePaymentResponse = async () => {
                    try {
                        // Store in payment_result store
                        const paymentResult = {
                            squareResponse: result,
                            paymentTimestamp: new Date().toISOString(),
                            paymentStatus: 'completed',
                            cardToken: token,
                            transactionId: result.transactionId || result.paymentId || 'N/A',
                            amount: result.amount || result.paymentAmount || 'N/A',
                            currency: currency,
                            selectedPlan: selectedPlan,
                            promoCode: promoCode,
                            promoApplied: promoApplied,
                            billingEmail: billingEmail,
                            billingPhone: billingPhone,
                            billingCity: billingCity,
                            billingState: billingState,
                            billingZip: billingZip,
                            billingCountry: billingCountry,
                            success: result.success || result.status === 'success'
                        };

                        await prysmDB.set('payment_result', 'current_payment', paymentResult);
                        console.log('[SquarePaymentCard] Payment result saved to storage:', paymentResult);

                        // Also update registration_data for backward compatibility
                        const registrationData = await prysmDB.get('registration_data', 'current_registration');
                        if (registrationData) {
                            registrationData.squarePaymentResponse = result;
                            registrationData.paymentTimestamp = new Date().toISOString();
                            registrationData.paymentStatus = 'completed';
                            registrationData.cardToken = token;
                            await prysmDB.set('registration_data', 'current_registration', registrationData);

                            // Dispatch event to notify other components
                            window.dispatchEvent(new CustomEvent('registrationDataUpdated', { detail: registrationData }));

                            console.log('[SquarePaymentCard] Payment response also saved to registration_data');
                        }
                    } catch (error) {
                        console.error('[SquarePaymentCard] Error saving payment result to storage:', error);
                    }
                };

                savePaymentResponse();

                if (onPaymentSuccess) {
                    onPaymentSuccess(result);
                }

                // Navigate to selfie upload page after successful payment
                navigate('/upload-selfie');
                return result;
            } else {
                throw new Error(result.message || 'Payment processing failed');
            }
        } catch (error) {
            console.error('[SquarePaymentCard] Payment processing error caught:', {
                error: error,
                message: error.message,
                squareErrorCode: error.squareErrorCode,
                squareErrorDetail: error.squareErrorDetail,
                squareErrorCategory: error.squareErrorCategory
            });
            
            // Directly show modal for payment errors
            console.log('[SquarePaymentCard] Directly setting modal error state');
            setModalError(error);
            setShowErrorModal(true);
            setPaymentFailed(true);
            setLastError(error);
            
            // Track payment processing error
            const paymentDuration = Date.now() - paymentStartTime;
            trackPayment.error({
                errorMessage: error.message,
                errorType: 'payment_processing',
                step: 'server_processing',
                processingDuration: paymentDuration,
                amount: finalAmount
            });
            
            // Track performance metric for failed payment
            trackPerformance('payment_processing_duration', paymentDuration, 'ms', {
                success: false,
                error: error.message
            });
            
            // Don't throw error to prevent further error handling
            return;
        } finally {
            setIsProcessingPayment(false);
        }
    }, [selectedPlan, plans, currency, config.backendUrl, onPaymentSuccess, onPaymentError]);

    const handlePayment = useCallback(async () => {
        // Check for existing successful payment first
        try {
            const existingPayment = await prysmDB.get('payment_result', 'current_payment');
            if (existingPayment && (existingPayment.success || existingPayment.paymentStatus === 'completed')) {
                console.log('[SquarePaymentCard] Payment already completed, preventing duplicate payment');
                handlePaymentError('Payment has already been successfully processed. You cannot submit another payment.');
                return;
            }

            // Also check registration data for payment completion (backward compatibility)
            const registrationData = await prysmDB.get('registration_data', 'current_registration');
            if (registrationData && (registrationData.paymentStatus === 'completed' || registrationData.paymentCompleted)) {
                console.log('[SquarePaymentCard] Payment already completed in registration data, preventing duplicate payment');
                handlePaymentError('Payment has already been successfully processed. You cannot submit another payment.');
                return;
            }
        } catch (error) {
            console.error('[SquarePaymentCard] Error checking existing payment status:', error);
            // Continue with payment if we can't check status
        }

        const card = cardInstanceRef.current;
        if (!card) {
            const errorMsg = 'Card form not ready';
            handlePaymentError(errorMsg);
            return;
        }

        // Pre-validate billing information before card tokenization
        const billingValidation = validateBillingData();
        if (!billingValidation.isValid) {
            console.warn('[SquarePaymentCard] Billing validation failed:', billingValidation.errors);
            setBillingErrors(billingValidation.errors);
            setError(`Please fix the following billing information errors:\n${billingValidation.errors.join('\n')}`);
            
            // Track form validation errors
            trackFormErrors('billing_address', billingValidation.errors);
            
            return;
        }

        // Clear billing errors if validation passes
        setBillingErrors([]);
        clearErrors();

        try {
            console.log('[SquarePaymentCard] Starting card tokenization');

            // Create billing address object for Square tokenization
            const billingAddress = {
                city: billingCity.trim(),
                state: billingState.trim(),
                zip: billingZip.trim(),
                country: billingCountry.trim()
            };

            // Calculate final amount for tokenization
            const currentPlan = plans[selectedPlan];
            const finalAmount = Math.max(0, currentPlan.price - promoDiscount);
            const verificationDetails = {
                amount: (finalAmount * 100).toString(),
                intent: 'CHARGE',
                currencyCode: 'USD',
                customerInitiated: true,  // required
                sellerKeyedIn: false,     // required
                billingContact: {
                    locality: billingAddress.city,
                    administrativeDistrictLevel1: billingAddress.state,
                    postalCode: billingAddress.zip,
                    country: billingAddress.country
                }
            };


            console.log('[SquarePaymentCard] Token details:', verificationDetails);

            const tokenizeStartTime = Date.now();
            const result = await card.tokenize(
                verificationDetails
            );
            const tokenizeDuration = Date.now() - tokenizeStartTime;

            if (result.status === 'OK' && result.token) {
                console.log('[SquarePaymentCard] Card tokenization successful');
                
                // Track successful Square tokenization
                trackSquare('tokenization_success', {
                    duration: tokenizeDuration,
                    amount: finalAmount,
                    hasToken: !!result.token
                });
                
                trackPerformance('square_tokenization_duration', tokenizeDuration, 'ms', {
                    success: true
                });
                
                handleTokenReceived(result.token, result);
            } else {
                console.error('[SquarePaymentCard] Tokenization failed:', result);
                console.log('[SquarePaymentCard] Tokenization errors:', result.errors);
                
                // Track Square tokenization failure
                trackSquare('tokenization_failure', {
                    duration: tokenizeDuration,
                    errors: result.errors || [],
                    status: result.status
                });
                
                trackPerformance('square_tokenization_duration', tokenizeDuration, 'ms', {
                    success: false,
                    errors: result.errors?.length || 0
                });
                
                handleSquareTokenizationError(result);
            }
        } catch (err) {
            console.error('[Outside SquarePaymentCard] Tokenization error:', err);
            console.log('[Outside SquarePaymentCard] Tokenization errors:', err.errors);
            handleSimpleError(err, 'Card Tokenization');
        }
    }, [handleTokenReceived, handlePaymentError, validateBillingData, billingCity, billingState, billingZip, billingCountry]);

    // Internal submit function (extracted from original handleSubmit)
    const handleSubmitInternal = useCallback(async () => {

        // Normal payment flow - check for token in state or passed parameter
        const tokenToUse = cardToken;
        if (!tokenToUse) {
            handlePaymentError('Please validate your card information first');
            return;
        }

        const billingValidation = validateBillingData();
        if (!billingValidation.isValid) {
            handlePaymentError(`Billing validation failed: ${billingValidation.errors.join(', ')}`);
            return;
        }

        try {
            const billingAddress = {
                email: billingEmail.trim(),
                phone: billingPhone.trim(),
                city: billingCity.trim(),
                state: billingState.trim(),
                zip: billingZip.trim(),
                country: billingCountry.trim()
            };
            await processPayment(tokenToUse, billingAddress);
        } catch (error) {
            // Error already handled in processPayment
        }
    }, [processPayment, handlePaymentError, promoApplied, promoCode, selectedPlan, plans, navigate, onPaymentSuccess, billingEmail, billingPhone, billingCity, billingState, billingZip, billingCountry]);

    /**
     * Process free registration when final amount is $0
     */
    const processFreeRegistration = useCallback(async () => {
        try {
            setIsProcessingPayment(true);
            
            const registrationData = await prysmDB.get('registration_data', 'current_registration');
            const visitorUUID = await getVisitorUUID();
            
            if (!visitorUUID) {
                throw new Error('Visitor UUID not found. Please refresh the page and try again.');
            }
            
            const freeRegistrationData = {
                'data-type': 'free-registration',
                visitorUUID: visitorUUID,
                email: registrationData?.email || billingEmail.trim(),
                amount: 0,
                promoCode: promoCode,
                planId: selectedPlan,
                planName: plans[selectedPlan].name,
                billingAddress: {
                    email: billingEmail.trim(),
                    phone: billingPhone.trim()
                },
                timestamp: new Date().toISOString()
            };
            
            // Call server API for free registration
            const response = await apiService.processFreeRegistration(freeRegistrationData);
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to process free registration');
            }
            
            const result = await response.json();
            
            // Save free registration result
            await prysmDB.set('payment_result', 'current_payment', {
                freeRegistration: true,
                amount: 0,
                promoCode: promoCode,
                success: true,
                timestamp: new Date().toISOString()
            });
            
            if (onPaymentSuccess) {
                onPaymentSuccess(result);
            }
            
            navigate('/upload-selfie');
        } catch (error) {
            handleSimpleError(error, 'Free Registration Processing');
        } finally {
            setIsProcessingPayment(false);
        }
    }, [selectedPlan, plans, promoCode, billingEmail, billingPhone, onPaymentSuccess, navigate]);

    // Consolidated function that validates card and submits payment in one step
    const handleConsolidatedPayment = useCallback(async () => {
        console.log('[SquarePaymentCard] Starting consolidated payment process (validate + submit)');
        
        // Prevent multiple simultaneous payment attempts
        if (isProcessingPayment || isSubmitting) {
            console.log('[SquarePaymentCard] Payment already in progress, ignoring duplicate request');
            return;
        }
        
        const currentPlan = plans[selectedPlan];
        const finalAmount = Math.max(0, currentPlan.price - promoDiscount);
        
        // Skip payment processing if final amount is $0
        if (finalAmount === 0) {
            console.log('[SquarePaymentCard] Final amount is $0, processing as free registration');
            await processFreeRegistration();
            return;
        }
        
        setIsSubmitting(true);
        
        try {
            // Always generate a fresh token for each payment submission
            console.log('[SquarePaymentCard] Generating fresh card token for payment submission...');

            // Clear any existing token to ensure fresh tokenization
            setCardToken(null);

            let tokenToUse = null;

            try {
                // Call handlePayment which will validate and set the token
                await handlePayment();

                // Wait for the token to be set in state by checking storage
                let attempts = 0;
                const maxAttempts = 50; // 5 seconds max wait

                while (!tokenToUse && attempts < maxAttempts) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                    attempts++;

                    // Try to get the token from storage
                    try {
                        const nonceInfo = await prysmDB.get('square_nonce_info', 'current_nonce');
                        if (nonceInfo && nonceInfo.token) {
                            tokenToUse = nonceInfo.token;
                            console.log('[SquarePaymentCard] Retrieved fresh token from storage:', tokenToUse);
                            break;
                        }
                    } catch (error) {
                        console.warn('[SquarePaymentCard] Could not retrieve token from storage:', error);
                    }
                }

                // Check if validation was successful
                if (!tokenToUse) {
                    console.error('[SquarePaymentCard] Card validation failed - no token received');
                    handlePaymentError('Card validation failed - please check your card information and try again');
                    return;
                }

                console.log('[SquarePaymentCard] Card validation successful, fresh token received');
            } catch (error) {
                console.error('[SquarePaymentCard] Card validation error:', error);
                handlePaymentError('Card validation failed: ' + error.message);
                return;
            }

            // Now proceed with payment submission using the token
            console.log('[SquarePaymentCard] Card validated, proceeding with payment submission...');

            // Normal payment flow
            const billingValidation = validateBillingData();
            if (!billingValidation.isValid) {
                handlePaymentError(`Billing validation failed: ${billingValidation.errors.join(', ')}`);
                return;
            }

            try {
                const billingAddress = {
                    email: billingEmail.trim(),
                    phone: billingPhone.trim(),
                    city: billingCity.trim(),
                    state: billingState.trim(),
                    zip: billingZip.trim(),
                    country: billingCountry.trim()
                };
                await processPayment(tokenToUse, billingAddress);
            } catch (error) {
                // Error already handled in processPayment
            }
        } finally {
            setIsSubmitting(false);
        }
    }, [cardToken, handlePayment, handlePaymentError, promoApplied, promoCode, selectedPlan, plans, navigate, onPaymentSuccess, billingEmail, billingPhone, billingCity, billingState, billingZip, billingCountry, processPayment, promoDiscount, processFreeRegistration]);

    // New handleSubmit that calls the consolidated payment function
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        await handleConsolidatedPayment();
    }, [handleConsolidatedPayment]);

    // Retry payment function
    const handleRetryPayment = useCallback(async () => {
        console.log('[SquarePaymentCard] Retrying payment, attempt:', retryCount + 1);
        
        setRetryCount(prev => prev + 1);
        clearErrors();
        
        await handleConsolidatedPayment();
    }, [retryCount, handleConsolidatedPayment, clearErrors]);



    // ================================
    // EFFECTS
    // ================================

    // Load promo code, selected plan, and billing address from storage on component mount
    useEffect(() => {
        const loadDataFromStorage = async () => {
            try {
                console.log('[SquarePaymentCard] Loading data from storage...');
                const registrationData = await prysmDB.get('registration_data', 'current_registration');
                console.log('[SquarePaymentCard] Registration data loaded:', {
                    hasData: !!registrationData,
                    hasEmail: !!registrationData?.email,
                    hasPhone: !!(registrationData?.mobile_number || registrationData?.mobileNumber),
                    hasCity: !!registrationData?.city,
                    hasState: !!registrationData?.state,
                    hasZip: !!registrationData?.zip_code,
                    hasCountry: !!registrationData?.country
                });

                if (registrationData) {
                    // Load promo code
                    if (registrationData.promoCode && registrationData.promoApplied) {
                        setPromoCode(registrationData.promoCode);
                        setPromoApplied(true);
                        setPromoDiscount(registrationData.promoDiscount || 0);
                        setPromoError('');
                        console.log('[SquarePaymentCard] Loaded promo code from storage:', {
                            code: registrationData.promoCode,
                            discount: registrationData.promoDiscount,
                            description: registrationData.promoDescription
                        });
                    }

                    // Load selected plan
                    if (registrationData.selectedPlan) {
                        setSelectedPlan(registrationData.selectedPlan);
                        console.log('[SquarePaymentCard] Loaded selected plan from storage:', {
                            planId: registrationData.selectedPlan,
                            planName: registrationData.selectedPlanName,
                            price: registrationData.selectedPlanPrice
                        });
                    }

                    // Load billing address from registration data
                    if (registrationData.email) {
                        setBillingEmail(registrationData.email);
                        console.log('[SquarePaymentCard] Loaded billing email from storage:', registrationData.email);
                    }
                    if (registrationData.mobile_number || registrationData.mobileNumber) {
                        const phoneNumber = registrationData.mobile_number || registrationData.mobileNumber;
                        // Format phone number to (XXX) XXX-XXXX format for Square validation
                        const formattedPhone = formatPhoneNumber(phoneNumber);
                        setBillingPhone(formattedPhone);
                        console.log('[SquarePaymentCard] Loaded and formatted billing phone from storage:', {
                            original: phoneNumber,
                            formatted: formattedPhone
                        });
                    }
                    if (registrationData.city) {
                        setBillingCity(registrationData.city);
                        console.log('[SquarePaymentCard] Loaded billing city from storage:', registrationData.city);
                    }
                    if (registrationData.state) {
                        setBillingState(registrationData.state);
                        console.log('[SquarePaymentCard] Loaded billing state from storage:', registrationData.state);
                    }
                    if (registrationData.zip_code) {
                        setBillingZip(registrationData.zip_code);
                        console.log('[SquarePaymentCard] Loaded billing zip from storage:', registrationData.zip_code);
                    }
                    if (registrationData.country) {
                        setBillingCountry(registrationData.country);
                        console.log('[SquarePaymentCard] Loaded billing country from storage:', registrationData.country);
                    }
                } else {
                    console.log('[SquarePaymentCard] No registration data found in storage');
                }

                // Reset card validation state on page load for security
                setCardToken(null);
                console.log('[SquarePaymentCard] Card validation state reset on page load');

                // Clear any existing Square nonce info from storage
                try {
                    await prysmDB.delete('square_nonce_info', 'current_nonce');
                    console.log('[SquarePaymentCard] Cleared Square nonce info from storage on page load');
                } catch (error) {
                    console.log('[SquarePaymentCard] No existing Square nonce info to clear:', error.message);
                }
            } catch (error) {
                console.error('[SquarePaymentCard] Error loading data from storage:', error);
            }
        };

        loadDataFromStorage();
    }, []);

    // Check for successful payment status on component mount
    useEffect(() => {
        const checkPaymentStatus = async () => {
            try {
                // Check the specific localStorage key mentioned by the user
                const paymentResult = await prysmDB.get('payment_result', 'current_payment');
                if (paymentResult && paymentResult.squareResponse && paymentResult.squareResponse.status === 'success') {
                    console.log('[SquarePaymentCard] Successful payment detected, disabling submit button');
                    setPaymentSubmitted(true);
                }
            } catch (error) {
                console.log('[SquarePaymentCard] No existing payment result found or error checking status:', error.message);
            }
        };

        checkPaymentStatus();
    }, []);

    // Initialize Square SDK and card component (based on working example)
    useEffect(() => {
        if (hasInitRef.current) return;         // guard against multiple runs
        hasInitRef.current = true;

        const initializeSquare = async () => {
            // Dynamically load Square SDK if not already loaded
            if (!window.Square) {
                try {
                    console.log('[SquarePaymentCard] Loading Square SDK dynamically', { sdkUrl: config.sdkUrl });

                    // Create and load the script
                    const script = document.createElement('script');
                    script.src = config.sdkUrl;
                    script.type = 'text/javascript';
                    script.async = true;

                    // Wait for script to load
                    await new Promise((resolve, reject) => {
                        script.onload = resolve;
                        script.onerror = () => reject(new Error(`Failed to load Square SDK from ${config.sdkUrl}`));
                        document.head.appendChild(script);
                    });

                    console.log('[SquarePaymentCard] Square SDK loaded successfully');
                } catch (err) {
                    handleSimpleError(err, 'Square SDK Loading');
                    setLoading(false);
                    return;
                }
            }

            if (!window.Square || !window.Square.payments) {
                const error = new Error('Square SDK not available');
                handleSimpleError(error, 'Square SDK Availability Check');
                setLoading(false);
                return;
            }

            // Set a timeout to prevent infinite loading
            let loadingTimeout = setTimeout(() => {
                if (loading) {
                    setLoading(false);
                    setLoadingError('Payment system initialization timed out. Please try again.');
                }
            }, 15000); // 15 second timeout

            try {
                setLoading(true);
                setLoadingError(null); // Clear any previous loading errors
                console.log('[SquarePaymentCard] Initializing Square Payments');

                // Log Square configuration for debugging
                console.log('🔍 SQUARE CONFIGURATION:', {
                    environment: config.environment,
                    applicationId: config.applicationId,
                    locationId: config.locationId,
                    sdkUrl: config.sdkUrl,
                    configured: !!(config.applicationId && config.locationId)
                });

                // prevent re-init if already set (extra guard)
                if (!paymentsRef.current) {
                    paymentsRef.current = window.Square.payments(
                        config.applicationId,
                        config.locationId
                    );
                }

                // Do not attach if already attached
                if (!cardInstanceRef.current) {
                    const card = await paymentsRef.current.card();

                    // Attach only if the container exists and is empty
                    if (cardRef.current && cardRef.current.childElementCount === 0) {
                        await card.attach(cardRef.current);
                        cardInstanceRef.current = card;
                        console.log('[SquarePaymentCard] Square Card initialized successfully');
                    } else {
                        console.log('[SquarePaymentCard] Skipped attach: container already has children or card exists');
                    }
                }

                // Clear the timeout since initialization completed successfully
                clearTimeout(loadingTimeout);
            } catch (err) {
                console.error('[SquarePaymentCard] Square SDK initialization failed:', err);
                setLoadingError(err.message || 'Failed to initialize payment system');
                handleSimpleError(err, 'Square Payments Initialization');
                clearTimeout(loadingTimeout);
            } finally {
                setLoading(false);
            }
        };

        void initializeSquare();

        return () => {
            // Best-effort cleanup to avoid ghost iframes on remount
            try {
                const card = cardInstanceRef.current;
                if (card?.destroy) {
                    card.destroy();
                }
            } catch { }
            if (cardRef.current) {
                cardRef.current.innerHTML = '';
            }
            cardInstanceRef.current = null;
            paymentsRef.current = null;
            hasInitRef.current = false;
            
            // Clear any pending promo code validation timeout
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
                debounceTimeoutRef.current = null;
            }
        };
        // IMPORTANT: empty deps — we control re-runs with hasInitRef
    }, []);

    // ================================
    // RENDER HELPERS
    // ================================

    const renderLoadingOverlay = () => {
        // Show loading error if there's a loading error
        if (loadingError) {
            return (
                <div className="absolute inset-0 bg-gray-900/95 backdrop-blur-sm flex items-center justify-center z-20 rounded-2xl">
                    <div className="text-center p-8 max-w-lg">
                        {/* Error icon */}
                        <div className="relative mb-6">
                            <div className="w-16 h-16 mx-auto bg-red-900/50 rounded-full flex items-center justify-center">
                                <svg className="w-8 h-8 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            </div>
                        </div>

                        {/* Error message */}
                        <div className="space-y-4">
                            <h3 className="text-xl font-semibold text-red-400 mb-2">
                                Payment System Initialization Failed
                            </h3>
                            <div className="p-4 bg-red-900/20 border border-red-700/50 rounded-lg">
                                <p className="text-red-200 text-sm mb-3">
                                    {loadingError}
                                </p>
                                <div className="text-xs text-red-300">
                                    <p className="font-medium mb-2">Possible causes:</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>Network connection issues</li>
                                        <li>Square SDK configuration problem</li>
                                        <li>Browser security restrictions</li>
                                        <li>Temporary service unavailability</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Retry button */}
                        <div className="mt-6">
                            <button
                                onClick={() => {
                                    setLoadingError(null);
                                    setLoading(true);
                                    // Trigger re-initialization by resetting the ref
                                    hasInitRef.current = false;
                                    // Re-initialize Square instead of reloading page
                                    setTimeout(() => {
                                        const initializeSquare = async () => {
                                            try {
                                                if (!paymentsRef.current) {
                                                    paymentsRef.current = window.Square.payments(
                                                        config.applicationId,
                                                        config.locationId
                                                    );
                                                }
                                                if (!cardInstanceRef.current && cardRef.current) {
                                                    const card = await paymentsRef.current.card();
                                                    await card.attach(cardRef.current);
                                                    cardInstanceRef.current = card;
                                                }
                                            } catch (err) {
                                                setLoadingError(err.message || 'Failed to initialize payment system');
                                            } finally {
                                                setLoading(false);
                                            }
                                        };
                                        initializeSquare();
                                    }, 100);
                                }}
                                className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors duration-200"
                            >
                                Retry Payment System
                            </button>
                        </div>

                        {/* Alternative options */}
                        <div className="mt-4 p-4 bg-blue-900/20 border border-blue-700/30 rounded-lg">
                            <div className="flex items-start space-x-2">
                                <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                <div className="text-sm text-blue-200">
                                    <p className="font-medium mb-1">Need help?</p>
                                    <p>Try refreshing the page or contact support if the problem persists.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        // Show normal loading if loading
        if (!loading) return null;

        return (
            <div className="absolute inset-0 bg-gray-900/90 backdrop-blur-sm flex items-center justify-center z-20 rounded-2xl">
                <div className="text-center p-8 max-w-md">
                    {/* Animated loading spinner */}
                    <div className="relative mb-6">
                        <div className="w-16 h-16 mx-auto">
                            {/* Outer ring */}
                            <div className="absolute inset-0 border-4 border-gray-700 rounded-full"></div>
                            {/* Animated ring */}
                            <div className="absolute inset-0 border-4 border-transparent border-t-purple-500 rounded-full animate-spin"></div>
                            {/* Inner pulsing dot */}
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-3 h-3 bg-purple-500 rounded-full animate-pulse"></div>
                            </div>
                        </div>
                    </div>

                    {/* Loading messages */}
                    <div className="space-y-2">
                        <h3 className="text-xl font-semibold text-white mb-2">
                            Initializing Payment System
                        </h3>
                        <div className="space-y-1 text-gray-300">
                            <div className="flex items-center justify-center space-x-2">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                <span className="text-sm">Loading Square Payment SDK</span>
                            </div>
                            <div className="flex items-center justify-center space-x-2">
                                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse animation-delay-1000"></div>
                                <span className="text-sm">Connecting to payment processor</span>
                            </div>
                            <div className="flex items-center justify-center space-x-2">
                                <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse animation-delay-2000"></div>
                                <span className="text-sm">Preparing secure payment form</span>
                            </div>
                        </div>
                    </div>

                    {/* Progress indicator */}
                    <div className="mt-6">
                        <div className="w-full bg-gray-700 rounded-full h-2">
                            <div className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full animate-pulse" style={{ width: '70%' }}></div>
                        </div>
                        <p className="text-xs text-gray-400 mt-2">
                            This usually takes just a few seconds...
                        </p>
                    </div>

                    {/* Help text */}
                    <div className="mt-6 p-4 bg-blue-900/20 border border-blue-700/30 rounded-lg">
                        <div className="flex items-start space-x-2">
                            <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            <div className="text-sm text-blue-200">
                                <p className="font-medium mb-1">What's happening?</p>
                                <p>We're securely loading the payment system. This ensures your card information is protected with bank-level security.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderError = () => {
        if (!error) return null;

        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <div>
                        <h3 className="text-sm font-medium text-red-800">Payment Error</h3>
                        <p className="text-sm text-red-700 mt-1">{error}</p>
                    </div>
                </div>
            </div>
        );
    };

    // ================================
    // RENDER
    // ================================

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center p-4 relative overflow-hidden">
            {/* Decorative background elements */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-900 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
                <div className="absolute top-40 left-40 w-80 h-80 bg-gray-800 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
            </div>

            <div className="w-full max-w-4xl relative z-10">
                <div className="bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-2xl p-8 transform transition-all duration-500 hover:scale-[1.02] border border-gray-700">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-500 mb-4 animate-fade-in">
                            Secure Payment
                        </h1>
                        <div className="h-1 w-20 bg-gradient-to-r from-pink-500 to-purple-600 mx-auto rounded-full animate-glow"></div>
                        <p className="mt-4 text-gray-400">
                            Complete your registration with secure payment processing
                        </p>

                        {/* Price Display */}
                        <div className="mt-6 p-4 bg-gray-700/50 rounded-xl border border-gray-600">
                            <div className="text-3xl font-bold text-white">
                                ${Math.max(0, plans[selectedPlan].price - promoDiscount).toFixed(2)} {currency}
                            </div>
                            {promoDiscount > 0 && (
                                <div className="text-sm text-green-400 mt-1">
                                    Save ${(promoDiscount / 1).toFixed(2)}
                                </div>
                            )}
                            <div className="text-sm text-gray-300 mt-1">
                                {plans[selectedPlan].name}
                            </div>
                        </div>
                    </div>

                    {/* Error Display */}
                    {renderError()}

                    {/* Subscription Plans */}
                    <div className="mb-8">
                        <h2 className="text-2xl font-bold text-gray-300 mb-2 text-center">Choose Your Plan</h2>
                        <p className="text-sm text-gray-400 mb-6 text-center">Unless Stated Otherwise Prices are Per Person</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {Object.values(plans).map((plan) => (
                                <div
                                    key={plan.id}
                                    className={`relative border-2 rounded-xl p-6 cursor-pointer transition-all duration-200 ${selectedPlan === plan.id
                                        ? 'border-pink-500 bg-gray-700/50 shadow-lg'
                                        : 'border-gray-600 hover:border-pink-400'
                                        }`}
                                    onClick={() => {
                                        setSelectedPlan(plan.id);

                                        // Store selected plan to storage
                                        const savePlanSelection = async () => {
                                            try {
                                                const registrationData = await prysmDB.get('registration_data', 'current_registration');
                                                if (registrationData) {
                                                    registrationData.selectedPlan = plan.id;
                                                    registrationData.selectedPlanName = plan.name;
                                                    registrationData.selectedPlanPrice = plan.price;
                                                    registrationData.selectedPlanPeriod = plan.period;
                                                    registrationData.selectedPlanRecurringPrice = plan.recurringPrice;
                                                    registrationData.planSelectionTimestamp = new Date().toISOString();
                                                    await prysmDB.set('registration_data', 'current_registration', registrationData);

                                                    // Dispatch event to notify other components
                                                    window.dispatchEvent(new CustomEvent('registrationDataUpdated', { detail: registrationData }));

                                                    console.log('[SquarePaymentCard] Plan selection saved to storage:', {
                                                        planId: plan.id,
                                                        planName: plan.name,
                                                        price: plan.price
                                                    });
                                                }
                                            } catch (error) {
                                                console.error('[SquarePaymentCard] Error saving plan selection to storage:', error);
                                            }
                                        };

                                        savePlanSelection();
                                    }}
                                >
                                    {/* Plan Badge */}
                                    {plan.savings && (
                                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                            <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                                                {plan.savings}
                                            </span>
                                        </div>
                                    )}

                                    {/* Plan Header */}
                                    <div className="text-center mb-4">
                                        <h3 className="text-xl font-bold text-gray-200">{plan.name}</h3>
                                        <p className="text-sm text-gray-400">{plan.tagline}</p>
                                    </div>

                                    {/* Pricing */}
                                    <div className="text-center mb-6">
                                        <div className="text-3xl font-bold text-white">
                                            ${(plan.price / 1).toFixed(2)}
                                        </div>
                                        <div className="text-sm text-gray-400">
                                            /{plan.period === 'month' ? 'first month per person' : '3 months per person'}
                                        </div>
                                        <div className="text-sm text-gray-500 mt-1">
                                            Then ${(plan.recurringPrice / 1).toFixed(2)}/{plan.period === 'month' ? 'month per person' : 'quarter per person'}
                                        </div>
                                    </div>

                                    {/* Features */}
                                    <ul className="space-y-2 mb-6">
                                        {plan.features.map((feature, index) => (
                                            <li key={index} className="flex items-start">
                                                <svg className="w-4 h-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                                <span className="text-sm text-gray-300">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    {/* Selection Indicator */}
                                    <div className="text-center">
                                        <div className={`inline-flex items-center justify-center w-6 h-6 rounded-full border-2 ${selectedPlan === plan.id
                                            ? 'border-pink-500 bg-pink-500'
                                            : 'border-gray-500'
                                            }`}>
                                            {selectedPlan === plan.id && (
                                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Contact Information Section */}
                    <div className="space-y-6">
                        <div>
                            <h3 className="text-xl font-medium text-gray-300 mb-4">Contact Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                {/* Email Field */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">
                                        Email Address *
                                    </label>
                                    <input
                                        type="email"
                                        value={billingEmail}
                                        onChange={(e) => {
                                            setBillingEmail(e.target.value);
                                            // Clear billing errors when user starts typing
                                            if (billingErrors.length > 0) {
                                                setBillingErrors([]);
                                                clearErrors();
                                            }
                                        }}
                                        placeholder="Enter email address"
                                        required
                                        disabled={isProcessing || isProcessingPayment}
                                        className={`w-full px-3 py-2 border rounded-xl bg-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 ${getFieldError('email')
                                            ? 'border-red-500 focus:ring-red-500'
                                            : 'border-gray-600 focus:ring-pink-500'
                                            }`}
                                    />
                                    {getFieldError('email') && (
                                        <p className="mt-1 text-sm text-red-400">{getFieldError('email')}</p>
                                    )}
                                </div>

                                {/* Phone Field */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">
                                        Phone Number *
                                    </label>
                                    <input
                                        type="tel"
                                        value={billingPhone}
                                        onChange={(e) => {
                                            let value = e.target.value.replace(/\D/g, '');
                                            if (value.length >= 6) {
                                                value = value.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
                                            } else if (value.length >= 3) {
                                                value = value.replace(/(\d{3})(\d{0,3})/, '($1) $2');
                                            }
                                            setBillingPhone(value);
                                            // Clear billing errors when user starts typing
                                            if (billingErrors.length > 0) {
                                                setBillingErrors([]);
                                                clearErrors();
                                            }
                                        }}
                                        placeholder="(XXX) XXX-XXXX"
                                        required
                                        disabled={isProcessing || isProcessingPayment}
                                        maxLength="14"
                                        className={`w-full px-3 py-2 border rounded-xl bg-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 ${getFieldError('phone')
                                            ? 'border-red-500 focus:ring-red-500'
                                            : 'border-gray-600 focus:ring-pink-500'
                                            }`}
                                    />
                                    {getFieldError('phone') && (
                                        <p className="mt-1 text-sm text-red-400">{getFieldError('phone')}</p>
                                    )}
                                </div>
                            </div>
                            <p className="text-xs text-gray-400">
                                Used for receipts and support communication.
                            </p>
                        </div>

                        {/* Credit Card & Billing Section - Only show if payment required */}
                        {(() => {
                            const currentPlan = plans[selectedPlan];
                            const finalAmount = Math.max(0, currentPlan.price - promoDiscount);
                            return finalAmount > 0 ? (
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Payment & Billing Information
                                    </label>
                                    <div
                                        ref={cardRef}
                                        className="min-h-[60px]"
                                        style={{ minHeight: '60px' }}
                                    />
                                    <p className="mt-2 text-xs text-gray-400">
                                        Square will securely collect your card and billing address information. Your data is encrypted and never stored on our servers.
                                    </p>
                                </div>
                            ) : (
                                <div className="bg-green-900/20 border border-green-700 rounded-xl p-4">
                                    <div className="flex items-center text-green-400">
                                        <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        <div>
                                            <p className="font-medium">🎉 Free Registration!</p>
                                            <p className="text-green-300 text-sm mt-1">Your promo code covers the full cost - no payment required!</p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Promotional Code Section */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-medium text-gray-300">Promotional Code</h3>

                            <div className="flex gap-3">
                                <div className="flex-1">
                                    <input
                                        type="text"
                                        value={promoCode}
                                        onChange={handlePromoCodeChange}
                                        placeholder="Enter promotional code"
                                        disabled={isProcessing || isProcessingPayment}
                                        className={`w-full px-3 py-2 border rounded-xl bg-gray-700 text-gray-200 focus:outline-none focus:ring-2 focus:ring-pink-500 transition-all duration-200 ${promoError
                                            ? 'border-red-400 bg-red-900/20'
                                            : promoApplied
                                                ? 'border-green-400 bg-green-900/20'
                                                : 'border-gray-600'
                                            }`}
                                    />

                                    {/* Promo Code Messages */}
                                    {promoError && (
                                        <p className="text-red-400 text-sm mt-1">{promoError}</p>
                                    )}

                                    {promoApplied && (
                                        <div className="flex items-center text-green-400 text-sm mt-1">
                                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                            Promo code applied! You save ${(promoDiscount / 1).toFixed(2)}
                                        </div>
                                    )}
                                </div>

                                {promoApplied ? (
                                    <button
                                        type="button"
                                        onClick={removePromoCode}
                                        className="px-4 py-2 text-sm text-gray-300 hover:text-white border border-gray-600 rounded-xl hover:bg-gray-700 transition-colors"
                                    >
                                        Remove
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => validatePromoCode(promoCode)}
                                        disabled={!promoCode.trim() || isProcessing || isProcessingPayment}
                                        className="px-4 py-2 text-sm text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-xl transition-colors"
                                    >
                                        Apply
                                    </button>
                                )}
                            </div>

                            {/* Price Breakdown */}
                            {promoDiscount > 0 && (
                                <div className="bg-gray-700/50 rounded-xl p-4 space-y-2 border border-gray-600">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-400">Original Price:</span>
                                        <span className="text-gray-200">${(plans[selectedPlan].price / 1).toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm text-green-400">
                                        <span>Discount ({promoCode}):</span>
                                        <span>-${(promoDiscount / 1).toFixed(2)}</span>
                                    </div>
                                    <div className="border-t border-gray-600 pt-2 flex justify-between font-semibold">
                                        <span className="text-gray-200">Total:</span>
                                        <span className="text-white">${Math.max(0, plans[selectedPlan].price - promoDiscount).toFixed(2)}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Enhanced Loading Overlay */}
                        {renderLoadingOverlay()}

                        {/* Payment Form */}
                        {!loading && (
                            <form onSubmit={handleSubmit} className="space-y-6">

                                {/* Validate Card Button - Hidden but functionality preserved */}
                                <div className="hidden">
                                    <button
                                        type="button"
                                        onClick={handlePayment}
                                        disabled={loading || isProcessing || isProcessingPayment}
                                        className={`px-6 py-3 rounded-lg font-medium transition-colors ${cardToken
                                            ? 'bg-green-600 text-white hover:bg-green-700'
                                            : 'bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed'
                                            }`}
                                    >
                                        {loading ? 'Initializing...' : cardToken ? '✓ Card Validated - Click to Re-validate' : 'Validate Card Information'}
                                    </button>
                                </div>

                                {/* Success message when card is validated - Hidden but preserved */}
                                {cardToken && (
                                    <div className="hidden">
                                        <div className="bg-green-900/20 border border-green-700 rounded-xl p-4">
                                            <div className="flex items-center text-green-400 text-sm">
                                                <svg className="w-5 h-5 mr-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                </svg>
                                                <div>
                                                    <p className="font-medium">✓ Card validated successfully!</p>
                                                    <p className="text-green-300 mt-1">Click on Submit Payment to proceed</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Submit Button */}
                                <div className="mt-6 space-y-3">
                                    <button
                                        type="submit"
                                        disabled={isProcessing || isProcessingPayment || isSubmitting || paymentSubmitted}
                                        className="w-full rounded-xl bg-gradient-to-r from-pink-500 to-purple-600 text-white px-4 py-3 font-semibold transition-all duration-200 hover:from-pink-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                    >
                                        {(() => {
                                            const currentPlan = plans[selectedPlan];
                                            const finalAmount = Math.max(0, currentPlan.price - promoDiscount);
                                            
                                            if (isSubmitting || isProcessingPayment) {
                                                return (
                                                    <>
                                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        Please Wait...
                                                    </>
                                                );
                                            }
                                            
                                            if (paymentSubmitted) {
                                                return 'Registration Submitted';
                                            }
                                            
                                            return finalAmount === 0 ? 'Complete Free Registration' : 'Submit Payment';
                                        })()}
                                    </button>
                                    

                                </div>
                            </form>
                        )}

                        {/* Simple Error Display - Only for non-payment errors */}
                        {error && (
                            <div className="mt-6 bg-red-900/20 border border-red-700 rounded-xl p-4">
                                <div className="flex items-center">
                                    <svg className="h-5 w-5 text-red-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                    <div className="flex-1">
                                        <p className="text-red-200 font-medium">{error}</p>
                                        <button
                                            onClick={clearErrors}
                                            className="text-sm text-red-300 hover:text-red-100 underline mt-2"
                                        >
                                            Try Again
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Error Modal for Payment Submission Errors */}
                        <ErrorModal
                            isOpen={showErrorModal}
                            onClose={() => {
                                setShowErrorModal(false);
                                setPaymentFailed(false);
                            }}
                            error={modalError}
                            title="Payment Failed"
                            showRetry={false}
                        />
                    </div>
                </div>
            </div>

            {/* Custom Styles */}
            <style jsx="true">{`
                @keyframes blob {
                    0% {
                        transform: translate(0px, 0px) scale(1);
                    }
                    33% {
                        transform: translate(30px, -50px) scale(1.1);
                    }
                    66% {
                        transform: translate(-20px, 20px) scale(0.9);
                    }
                    100% {
                        transform: translate(0px, 0px) scale(1);
                    }
                }
                .animate-blob {
                    animation: blob 7s infinite;
                }
                .animation-delay-2000 {
                    animation-delay: 2s;
                }
                .animation-delay-4000 {
                    animation-delay: 4s;
                }
                @keyframes glow {
                    0% {
                        box-shadow: 0 0 5px rgba(147, 51, 234, 0.2), 0 0 10px rgba(147, 51, 234, 0.2);
                    }
                    50% {
                        box-shadow: 0 0 20px rgba(147, 51, 234, 0.4), 0 0 30px rgba(147, 51, 234, 0.4);
                    }
                    100% {
                        box-shadow: 0 0 5px rgba(147, 51, 234, 0.2), 0 0 10px rgba(147, 51, 234, 0.2);
                    }
                }
                .animate-glow {
                    animation: glow 2s infinite;
                }
                @keyframes fade-in {
                    from {
                        opacity: 0;
                        transform: translateY(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fade-in {
                    animation: fade-in 1s ease-out;
                }
            `}</style>
        </div>
    );
};

export default SquarePaymentCard;